import { useEffect, useState } from "react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { Search, Globe2, Smartphone, Monitor } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import { loadCountries } from "@/lib/meta";

export default function RankChecker() {
  const [form, setForm] = useState({ domain: "", keyword: "", country: "US", device: "desktop", email: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [countries, setCountries] = useState({ items: [], common: [] });
  useEffect(() => { loadCountries().then(setCountries); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/tools/rank-check", form);
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rank check failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <SEO pageKey="tools_rank" title="Free Rank Checker — SerpNex" description="Instantly check where your domain ranks for any keyword on Google. Mobile or desktop, any country. Free, no signup — just an email." canonical="/tools/rank-checker" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="eyebrow">Free tool</div>
        <h1 className="mt-3 h-display text-4xl sm:text-5xl font-bold text-[#0A0D14]">Rank Checker</h1>
        <p className="mt-3 text-[#525B6A]">Where does your domain rank for a specific keyword? One check, one honest answer.</p>

        <form onSubmit={submit} className="mt-10 card-crisp p-6 space-y-4" data-testid="rank-checker-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Domain</label>
              <input required data-testid="rc-domain" placeholder="example.com" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Keyword</label>
              <input required data-testid="rc-keyword" placeholder="best rank tracker" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Country</label>
              <SearchableSelect
                items={countries.items}
                common={countries.common}
                value={form.country}
                onChange={(v) => setForm({ ...form, country: v })}
                testid="rc-country"
                placeholder="Select country"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Device</label>
              <div className="flex gap-2">
                <button type="button" data-testid="rc-device-desktop" onClick={() => setForm({ ...form, device: "desktop" })} className={`flex-1 px-3 py-2.5 border rounded-md text-sm inline-flex items-center justify-center gap-1.5 ${form.device === 'desktop' ? 'border-[#0047FF] bg-[#E0E7FF] text-[#0047FF]' : 'border-[#E2E8F0] text-[#525B6A]'}`}><Monitor className="h-3.5 w-3.5" />Desktop</button>
                <button type="button" data-testid="rc-device-mobile" onClick={() => setForm({ ...form, device: "mobile" })} className={`flex-1 px-3 py-2.5 border rounded-md text-sm inline-flex items-center justify-center gap-1.5 ${form.device === 'mobile' ? 'border-[#0047FF] bg-[#E0E7FF] text-[#0047FF]' : 'border-[#E2E8F0] text-[#525B6A]'}`}><Smartphone className="h-3.5 w-3.5" />Mobile</button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Your email <span className="text-[#8A94A6]">(gated tool)</span></label>
              <input required type="email" data-testid="rc-email" placeholder="you@agency.com" className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <button disabled={busy} data-testid="rc-submit" className="btn-primary px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2">
            {busy ? "Checking…" : (<><Search className="h-4 w-4" /> Check ranking</>)}
          </button>
        </form>

        {result && (
          <div className="mt-8 card-crisp p-8" data-testid="rc-result">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-[#8A94A6] uppercase tracking-wider">Current rank</div>
                <div className="stat-value text-6xl font-bold text-[#0047FF] mt-2">
                  {result.rank ? `#${result.rank}` : "—"}
                </div>
                <div className="mt-1 text-sm text-[#525B6A]">{result.rank ? "for" : "Not in top 100 for"} <span className="font-medium text-[#0A0D14]">{result.keyword}</span></div>
              </div>
              <div className="text-right">
                <div className="pill pill-accent"><Globe2 className="h-3 w-3" /> {result.country} · {result.device}</div>
                <div className="mt-2 text-xs text-[#8A94A6]">Checked {new Date(result.checked_at).toLocaleTimeString()}</div>
              </div>
            </div>
            {result.landing_url && (
              <div className="mt-6 border-t border-[#E2E8F0] pt-4 text-sm">
                <div className="text-xs text-[#8A94A6] uppercase tracking-wider">Ranking URL</div>
                <div className="text-[#0047FF] break-all mt-1">{result.landing_url}</div>
              </div>
            )}
            <div className="mt-6 border-t border-[#E2E8F0] pt-4 flex items-center justify-between">
              <div className="text-sm text-[#525B6A]">Want to track this daily?</div>
              <a href="/signup" className="btn-primary px-4 py-2 rounded-md text-sm">Start Free →</a>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
